import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/superadmin.css';

const SuperAdminLayout = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isPinned, setIsPinned] = useState(() => {
        const saved = localStorage.getItem('saas_superadmin_sidebar_pinned');
        // Default to true (pinned) if not set, or read from localStorage
        return saved ? saved === 'true' : true;
    });
    const [isHovered, setIsHovered] = useState(() => {
        return sessionStorage.getItem('saas_superadmin_sidebar_hovered') === 'true';
    });
    const [isMobileOpen, setIsMobileOpen] = useState(false);

    const sidebarRef = useRef(null);

    useEffect(() => {
        if (sidebarRef.current) {
            const savedScroll = sessionStorage.getItem('saas_sidebar_scroll');
            if (savedScroll) {
                sidebarRef.current.scrollTop = parseInt(savedScroll, 10);
            }
        }
    }, []);

    const handleScroll = (e) => {
        sessionStorage.setItem('saas_sidebar_scroll', e.target.scrollTop);
    };

    const toggleSidebar = () => {
        if (window.innerWidth <= 1024) {
            setIsMobileOpen(prev => !prev);
        } else {
            setIsPinned(prev => {
                const next = !prev;
                localStorage.setItem('saas_superadmin_sidebar_pinned', String(next));
                return next;
            });
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('saas_superadmin_token');
        localStorage.removeItem('saas_superadmin_user');
        navigate('/superadmin_v2_secure_login');
    };

    const navItems = [
        { group: 'Core' },
        { path: '/superadmin_v2_secure', icon: 'fa-home', label: 'Dashboard' },
        { path: '/superadmin_v2_secure/analytics', icon: 'fa-chart-line', label: 'Analytics' },
        { path: '/superadmin_v2_secure/payment_history', icon: 'fa-history', label: 'Payments' },
        
        { group: 'Partner Control' },
        { path: '/superadmin_v2_secure/add_customer', icon: 'fa-user-plus', label: 'Add Customer' },
        { path: '/superadmin_v2_secure/manage_superadmins', icon: 'fa-user-shield', label: 'Manage Admins' },
        { path: '/superadmin_v2_secure/manage_saas_plans', icon: 'fa-tags', label: 'SaaS Plans' },
        { path: '/superadmin_v2_secure/manage_saas_coupons', icon: 'fa-ticket-alt', label: 'Coupons' },
        { path: '/superadmin_v2_secure/manage_sponsors', icon: 'fa-handshake', label: 'Sponsors & KYC' },
        { path: '/superadmin_v2_secure/manage_ads', icon: 'fa-ad', label: 'Ad Campaigns' },
        { path: '/superadmin_v2_secure/ad_analytics', icon: 'fa-chart-pie', label: 'Ad Analytics' },
        { path: '/superadmin_v2_secure/manage_locations', icon: 'fa-map-marker-alt', label: 'Locations' },
        { path: '/superadmin_v2_secure/import_menu', icon: 'fa-file-import', label: 'Import Menu' },
        { path: '/superadmin_v2_secure/scan_menu', icon: 'fa-magic', label: 'Scan AI Menu' },
        { path: '/superadmin_v2_secure/leads', icon: 'fa-address-book', label: 'Contact Leads' },
        
        { group: 'Engagement' },
        { path: '/superadmin_v2_secure/manage_notifications', icon: 'fa-bell', label: 'Push Notifications' },
        { path: '/superadmin_v2_secure/manage_announcements', icon: 'fa-bullhorn', label: 'Announcements' },
        { path: '/superadmin_v2_secure/reviews', icon: 'fa-star', label: 'Testimonials' },
        
        { group: 'System' },
        { path: '/superadmin_v2_secure/manage_qr_templates', icon: 'fa-qrcode', label: 'QR Templates' },
        { path: '/superadmin_v2_secure/image_requests', icon: 'fa-image', label: 'Image Requests' },
        { path: '/superadmin_v2_secure/project_links', icon: 'fa-link', label: 'Project Links' },
        { path: '/superadmin_v2_secure/manage_saas_settings', icon: 'fa-cogs', label: 'Global Settings' }
    ];

    return (
        <div className="superadmin-container">
            <header className="sa-header">
                <div className="sa-logo" style={{ cursor: 'pointer' }} onClick={toggleSidebar}>
                    <i className="fas fa-bars" style={{ fontSize: '1.2rem', color: 'var(--text-secondary)', marginRight: '10px' }}></i>
                    <div className="sa-logo-icon">S</div>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)' }}>SIE QR Menu</h1>
                        <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Super Admin Panel</p>
                    </div>
                </div>
                <button onClick={handleLogout} className="sa-secondary-btn">
                    <i className="fas fa-sign-out-alt"></i> Logout
                </button>
            </header>

            <div className="sa-layout">
                {/* Mobile Drawer Overlay */}
                {isMobileOpen && (
                    <div className="sa-sidebar-overlay" onClick={() => setIsMobileOpen(false)}></div>
                )}

                {/* Enterprise Sidebar */}
                <aside 
                    ref={sidebarRef}
                    onScroll={handleScroll}
                    className={`sa-sidebar ${(!isPinned && !isHovered) ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}
                    onMouseEnter={() => {
                        setIsHovered(true);
                        sessionStorage.setItem('saas_superadmin_sidebar_hovered', 'true');
                    }}
                    onMouseLeave={() => {
                        setIsHovered(false);
                        sessionStorage.setItem('saas_superadmin_sidebar_hovered', 'false');
                    }}
                >
                    {/* Mobile Header inside Sidebar */}
                    <div className="sa-sidebar-mobile-header" style={{ display: 'none' }}>
                        <div className="sa-logo">
                            <div className="sa-logo-icon">S</div>
                            <div>
                                <h1 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)' }}>SIE</h1>
                            </div>
                        </div>
                        <button className="sa-modal-close" onClick={() => setIsMobileOpen(false)}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>

                    {navItems.map((item, index) => {
                        if (item.group) {
                            return (
                                <div key={`group-${index}`} className="sidebar-group-title" style={{ padding: '0.5rem 1rem', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: index === 0 ? '0.5rem' : '1rem' }}>
                                    {item.group}
                                </div>
                            );
                        }

                        // Determine if active. For dashboard, exact match needed to prevent matching everything.
                        const isActive = item.path === '/superadmin_v2_secure' 
                            ? location.pathname === '/superadmin_v2_secure' || location.pathname === '/superadmin_v2_secure/'
                            : location.pathname.startsWith(item.path);

                        return (
                            <button 
                                key={item.path} 
                                className={`sa-nav-item ${isActive ? 'active' : ''}`} 
                                onClick={() => {
                                    navigate(item.path);
                                    if (window.innerWidth <= 1024) setIsMobileOpen(false);
                                }} 
                                title={item.label}
                            >
                                <i className={`fas ${item.icon}`}></i> 
                                <span style={isActive ? { color: 'var(--primary-color)' } : {}}>{item.label}</span>
                            </button>
                        );
                    })}
                </aside>

                <main className="sa-main">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default SuperAdminLayout;
